import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, BellRing, Check, CheckCheck } from "lucide-react";
import { api } from "../lib/api";
import { formatLocalDateTime } from "../lib/constants";
import { toast } from "sonner";

const POLL_MS = 30000;

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const seenIdsRef = useRef(new Set());

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      // Toast newly-arrived items (not on first load)
      if (seenIdsRef.current.size > 0) {
        for (const n of data) {
          if (!seenIdsRef.current.has(n.id)) {
            toast.success(n.message, { duration: 6000 });
          }
        }
      }
      seenIdsRef.current = new Set(data.map((n) => n.id));
      setItems(data);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, []);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems((curr) => curr.filter((n) => n.id !== id));
    } catch {}
  };
  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems([]);
    } catch {}
  };

  const count = items.length;

  return (
    <div ref={wrapRef} className="relative">
      <button
        onClick={() => setOpen((s) => !s)}
        data-testid="notification-bell"
        className="relative h-10 w-10 grid place-items-center border-2 border-slate-700 hover:border-amber-500 hover:bg-slate-800 rounded-sm text-slate-200"
        aria-label="Notifications"
      >
        {count > 0 ? (
          <BellRing strokeWidth={2.5} className="h-4 w-4 text-amber-400" />
        ) : (
          <Bell strokeWidth={2.5} className="h-4 w-4" />
        )}
        {count > 0 && (
          <span
            className="absolute -top-1.5 -right-1.5 h-5 min-w-5 px-1 grid place-items-center bg-amber-500 text-slate-900 text-[10px] font-bold rounded-sm border-2 border-slate-900"
            data-testid="notification-badge"
          >
            {count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-12 w-80 max-w-[92vw] bg-white border-2 border-slate-300 rounded-sm shadow-2xl z-50"
          data-testid="notification-panel"
        >
          <div className="p-3 border-b-2 border-slate-200 flex items-center justify-between">
            <div className="font-display text-sm uppercase tracking-wider text-slate-900">
              Notifications
            </div>
            {count > 0 && (
              <button
                onClick={markAllRead}
                data-testid="notification-mark-all-read"
                className="text-xs font-bold uppercase tracking-wide text-slate-600 hover:text-slate-900 flex items-center gap-1"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Clear all
              </button>
            )}
          </div>
          {count === 0 ? (
            <div className="p-6 text-center text-slate-500 text-sm" data-testid="notification-empty">
              No new notifications.
            </div>
          ) : (
            <ul className="max-h-96 overflow-y-auto" data-testid="notification-list">
              {items.map((n) => (
                <li key={n.id} className="border-b border-slate-100 last:border-b-0">
                  <div className="p-3 flex gap-2 items-start">
                    <Link
                      to={`/shipments/${n.shipment_id}`}
                      onClick={() => setOpen(false)}
                      className="flex-1 min-w-0 hover:bg-slate-50 -m-1 p-1 rounded-sm"
                      data-testid={`notification-item-${n.id}`}
                    >
                      <div className="text-sm text-slate-900 leading-tight">{n.message}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {formatLocalDateTime(n.created_at)} · {n.created_by_name}
                      </div>
                    </Link>
                    <button
                      onClick={() => markRead(n.id)}
                      data-testid={`notification-read-${n.id}`}
                      title="Mark read"
                      className="h-7 w-7 grid place-items-center border-2 border-slate-200 hover:border-slate-900 rounded-sm text-slate-500 hover:text-slate-900 flex-shrink-0"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
